import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Plus, Trash2, Save, RotateCcw } from 'lucide-react';
import { db } from '@/lib/firebase';
import { Photo } from '@/components/ui/Photo';
import { SEED_FLEET, type FleetData } from '@/lib/useFleet';
import type { Vehicle } from '@/data/fleet';

const uid = () => Math.random().toString(36).slice(2, 9);
const clone = (f: FleetData): FleetData => JSON.parse(JSON.stringify(f));

export function Fleet() {
  const [fleet, setFleet] = useState<FleetData>(clone(SEED_FLEET));
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'fleet', 'list'));
        if (snap.exists()) {
          const d = snap.data() as Partial<FleetData>;
          if (d.vehicles?.length) setFleet({ vehicles: d.vehicles });
        }
      } catch {
        /* keep seed */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  function update(i: number, patch: Partial<Vehicle>) {
    setFleet((f) => ({ vehicles: f.vehicles.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
  }
  function addVehicle() {
    setFleet((f) => ({ vehicles: [...f.vehicles, { id: uid(), name: 'New vehicle', category: 'Sedan', pricePerDay: 0, active: true }] }));
  }
  function removeVehicle(i: number) {
    setFleet((f) => ({ vehicles: f.vehicles.filter((_, j) => j !== i) }));
  }

  async function save() {
    setBusy(true);
    try {
      // Strip undefined so Firestore accepts the array cleanly.
      const vehicles = fleet.vehicles.map((v) => {
        const o: Record<string, unknown> = { id: v.id || uid(), name: v.name, category: v.category, pricePerDay: Number(v.pricePerDay || 0) };
        if (v.seats != null && !Number.isNaN(v.seats)) o.seats = Number(v.seats);
        if (v.transmission) o.transmission = v.transmission;
        if (v.fuel) o.fuel = v.fuel;
        if (v.blurb) o.blurb = v.blurb;
        if (v.features && v.features.length) o.features = v.features;
        if (v.image) o.image = v.image;
        o.active = v.active !== false;
        return o;
      });
      await setDoc(doc(db, 'fleet', 'list'), { vehicles, updatedAt: new Date().toISOString() });
      flash('Saved. The website fleet is updated.');
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <p className="font-body text-sm text-muted">Loading fleet…</p>;

  return (
    <div className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-espresso">Fleet</h1>
          <p className="mt-1 font-body text-sm text-muted">Add and price the vehicles shown on the Car Hire page. Changes appear on the website on save.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setFleet(clone(SEED_FLEET))} className="btn-outline !px-4 !py-2.5"><RotateCcw size={15} /> Reset to defaults</button>
          <button onClick={save} disabled={busy} className="btn-primary !px-5 !py-2.5"><Save size={15} /> {busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <div className="mt-8 space-y-5">
        {fleet.vehicles.map((v, i) => (
          <section key={v.id} className="border border-line bg-paper p-5">
            <div className="grid gap-5 sm:grid-cols-[140px_1fr]">
              {/* Preview */}
              <div>
                <div className="aspect-[4/3] w-full overflow-hidden border border-line bg-cream">
                  <Photo src={v.image || ''} alt={v.name} className="h-full w-full" />
                </div>
                <label className="mt-2 flex items-center gap-2 font-body text-xs text-muted">
                  <input type="checkbox" checked={v.active !== false} onChange={(e) => update(i, { active: e.target.checked })} />
                  Show on website
                </label>
              </div>

              {/* Fields */}
              <div className="space-y-2.5">
                <div className="flex items-start gap-2">
                  <input className="field !py-2 flex-1 font-display !text-lg" value={v.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Vehicle name" />
                  <button onClick={() => removeVehicle(i)} className="mt-1.5 text-muted hover:text-blush-deep" aria-label="Remove vehicle"><Trash2 size={16} /></button>
                </div>
                <div className="grid gap-2.5 sm:grid-cols-3">
                  <div>
                    <label className="label">Category</label>
                    <input className="field !py-2" value={v.category} onChange={(e) => update(i, { category: e.target.value })} placeholder="SUV" />
                  </div>
                  <div>
                    <label className="label">Price / day (P)</label>
                    <input className="field !py-2" type="number" value={v.pricePerDay} onChange={(e) => update(i, { pricePerDay: Number(e.target.value) })} placeholder="0" />
                  </div>
                  <div>
                    <label className="label">Seats</label>
                    <input className="field !py-2" type="number" value={v.seats ?? ''} onChange={(e) => update(i, { seats: e.target.value ? Number(e.target.value) : undefined })} placeholder="5" />
                  </div>
                  <div>
                    <label className="label">Transmission</label>
                    <input className="field !py-2" value={v.transmission ?? ''} onChange={(e) => update(i, { transmission: e.target.value || undefined })} placeholder="Automatic" />
                  </div>
                  <div>
                    <label className="label">Fuel</label>
                    <input className="field !py-2" value={v.fuel ?? ''} onChange={(e) => update(i, { fuel: e.target.value || undefined })} placeholder="Petrol" />
                  </div>
                  <div>
                    <label className="label">Features (comma-separated)</label>
                    <input className="field !py-2" value={(v.features || []).join(', ')} onChange={(e) => update(i, { features: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="4x4, Air-con" />
                  </div>
                </div>
                <div>
                  <label className="label">Blurb</label>
                  <input className="field !py-2 !text-sm" value={v.blurb ?? ''} onChange={(e) => update(i, { blurb: e.target.value || undefined })} placeholder="Short description shown on the fleet card" />
                </div>
                <div>
                  <label className="label">Image URL</label>
                  <input className="field !py-2 !text-sm" type="url" value={v.image ?? ''} onChange={(e) => update(i, { image: e.target.value || undefined })} placeholder="https://…/car.jpg" />
                </div>
              </div>
            </div>
          </section>
        ))}

        <button onClick={addVehicle} className="inline-flex items-center gap-2 border border-dashed border-line bg-paper px-5 py-3 font-body text-sm text-espresso hover:border-gold"><Plus size={16} /> Add vehicle</button>
      </div>

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
